import { AdropParamsFactory } from '@/entities/advertisement';
import useGetBannerAdExchange from '@/hooks/advertisement/useGetBannerAdExchange';
import useHandleAdExposure from '@/hooks/advertisement/useHandleAdExposure';
import useLogger from '@/hooks/useLogger';
import { AdPageType } from '@/models/ad/list';
import { Client } from '@/types/app';
import { useCallback } from 'react';

const convertAdViewerOsPlatformByClient = (client: Client) => {
  if (!client.isWebView) {
    return 'web';
  }
  if (client.isAndroid) {
    return 'android';
  }
  return 'ios';
};

const useAdBanner = (adPageType: AdPageType, client: Client, enabled = true) => {
  const logger = useLogger();
  const factory = new AdropParamsFactory(adPageType, client);
  const targetType = factory.getInnerUnitId();

  const {
    bannerAdExchange,
    refetchBannerAdExchange,
    dataUpdatedAt,
    isLoading: isAdExchangeLoading,
  } = useGetBannerAdExchange(targetType, client, enabled);
  const { sendExposureLog } = useHandleAdExposure();

  const handleSendExposureLog = useCallback(() => {
    const adViewerOsPlatform = convertAdViewerOsPlatformByClient(client);
    const exposedAt = new Date().valueOf();

    if (bannerAdExchange && !!dataUpdatedAt) {
      logger.log({
        eventName: 'ad_banner_impression',
        payload: {
          targetType: bannerAdExchange.targetPlace,
          bannerAdId: bannerAdExchange.bannerAdId,
          adViewerOsPlatform: adViewerOsPlatform,
          exposedAt: exposedAt,
        },
      });
      sendExposureLog(bannerAdExchange.bannerAdId, exposedAt, adViewerOsPlatform);
    }
  }, [bannerAdExchange, dataUpdatedAt, client]);

  return {
    isAdExchangeLoading,
    handleSendExposureLog,
    bannerAdExchange,
    refetchBannerAdExchange,
    dataUpdatedAt,
  };
};

export default useAdBanner;
